import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { WaitingRoomPage } from '@/features/webinar/types';
import { useEffect, useState } from 'react';

type QuizFormValues = {
  name: string;
  subtitle: string;
  questions: {
    id?: number;
    textContent: string;
    type: 'SINGLE_CHOICE';
    options: {
      textContent: string;
      correct: boolean;
      position?: number | null;
    }[];
  }[];
};

interface PreviewQuizProps {
  form: UseFormReturn<QuizFormValues>;
  visualIdentity: WorkspaceVisualIdentity;
  currentWaitingRoomPage?: WaitingRoomPage | null;
}

export default function PreviewQuiz({ form, visualIdentity }: PreviewQuizProps) {
  const theme = visualIdentity?.theme;
  const { t } = useTranslation('webinars');
  const [quizStep, setQuizStep] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState<{
    [questionIndex: number]: number;
  }>({});

  const questions = form.watch('questions') || [];
  const currentQuestion = questions[quizStep - 1];

  useEffect(() => {
    if (quizStep > questions.length && questions.length > 0) {
      setQuizStep(questions.length);
    }
  }, [questions.length, quizStep]);

  return (
    <div className="flex flex-col   gap-4 w-full">
      <h3
        className="text-xl font-medium"
        style={{
          color: theme === 'DARK' ? 'white' : 'black',
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {form.watch('name') || t('waitingRoom.previewPlaceholders.quizTitle')}
      </h3>

      <p
        className="text-sm"
        style={{
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
          color: theme === 'DARK' ? 'white' : '#6D6E7A',
        }}
      >
        {form.watch('subtitle') || t('waitingRoom.previewPlaceholders.quizSubtitle')}
      </p>

      <div className="flex flex-col gap-4">
        {questions.length > 0 && currentQuestion ? (
          <div>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                {quizStep > 1 && (
                  <ArrowLeftIcon
                    className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                    onClick={() => {
                      setQuizStep(quizStep - 1);
                    }}
                  />
                )}
                <h3
                  className="text-base font-medium"
                  style={{
                    color: theme === 'DARK' ? 'white' : 'black',
                    fontFamily: visualIdentity?.fontFamily || 'Poppins',
                  }}
                >
                  <span
                    style={{
                      color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {quizStep}/{questions.length}
                  </span>{' '}
                  {currentQuestion.textContent || t('waitingRoom.survey.untitledQuestion')}
                </h3>
              </div>
              {quizStep < questions.length && (
                <ArrowRightIcon
                  className="size-6 text-[#9E9FA6] cursor-pointer"
                  onClick={() => {
                    setQuizStep(quizStep + 1);
                  }}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <RadioGroup
                value={selectedAnswer[quizStep - 1]?.toString() || ''}
                onValueChange={(value) => {
                  setSelectedAnswer((prev) => ({
                    ...prev,
                    [quizStep - 1]: parseInt(value, 10),
                  }));
                }}
                className="space-y-2"
              >
                {currentQuestion.options.map((option, optionIndex) => (
                  <div
                    key={optionIndex}
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{
                      backgroundColor:
                        theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <RadioGroupItem
                      value={optionIndex.toString()}
                      id={`quiz-${quizStep - 1}-${optionIndex}`}
                    />
                    <label
                      htmlFor={`quiz-${quizStep - 1}-${optionIndex}`}
                      className="text-base cursor-pointer flex-1"
                      style={{
                        color: theme === 'DARK' ? 'white' : 'black',
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {option.textContent || t('waitingRoom.survey.emptyAnswer')}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p
              className="text-sm"
              style={{
                color: theme === 'DARK' ? '#6D6E7A' : '#6D6E7A',
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {t('waitingRoom.survey.noQuestions')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
